import React, { useState, useEffect, useCallback, Fragment } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Stack,
    FormControlLabel,
    Switch,
    Button,
} from "@mui/material";
import PropTypes from "prop-types";

import { Box } from "@mui/system";

import RichTextEditor from "./RichTextEditor";
import Loader from "src/components/loader/Loader";
import Stepper from "./Stepper";

import { useDispatch } from "react-redux";
import { showAlert } from "src/redux/slices/AlertSlice";
import { useSelector } from "react-redux";

import { post, get } from "src/services/content.service";

import AttachmentsStep from "./AttachmentsStep";
import NavigationStep from "./Navigation/NavigationStep";
import filesize from "filesize";
import ThumbnailStep from "./ThumbnailStep";
import StickyForm from "./StickyForm";
import GroupSelector from "../Group/GroupSelector";
import { useTranslation } from 'react-i18next';

const NAV_TYPES = ["content_list", "nested_list", "tab"];
const ROOT_ALLOWED_TYPES = ["content_list", "nested_list", "tab", "list", "content"];
const PARENT_CHILD_RULES = {
    nested_list: ["tab", "content_list", "list", "content"],
    tab: ["content_list", "content"],
    content_list: ["content"],
    list: ["content_list", "content"],
};

const ContentDialog = ({
    open,
    onClose,
    type,
    contentType,
    slug,
    creationContext,
    submitStatus,
}) => {
    const { user } = useSelector((state) => state.auth);
    const [data, setData] = useState({
        userId: user.id,
        id: null,
        type: contentType ? contentType : null,
        status: false,
        title: "",
        body: null,
        sticky: 0,
    });
    const [navAll, setNavAll] = useState(null);
    const [removedNodes, setRemovedNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [thumbnail, setThumbnail] = useState(null);
    const [dialogTitle, setDialogTitle] = useState("");
    const [isOpened, setIsOpened] = useState(false);
    const [newNav, setNewNav] = useState(null);
    const [navElements, setNavElements] = useState(null);
    const [resolvedCreationContext, setResolvedCreationContext] = useState(null);

    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [removedFiles, setRemovedFiles] = useState([]);
    const [modifiedFiles, setModifiedFiles] = useState([]);
    const [rows, setRows] = useState([])
    const [changedNavIds, setChangedNavIds] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const { t } = useTranslation();

    const dispatch = useDispatch();

    const cleanup = () => {
        setData({
            userId: null,
            id: null,
            type: null,
            status: false,
            title: "",
            body: null,
            sticky: 0,
        });
        setNavAll(null);
        setLoading(false);
        setThumbnail(null);
        setDialogTitle("");
        setIsOpened(false);
        setNewNav(null);
        setRemovedNodes([]);
        setUploadedFiles([]);
        setRemovedFiles([]);
        setModifiedFiles([]);
        setRows([]);
        setSelectedGroups([]);
        setChangedNavIds([]);
        setResolvedCreationContext(null);
    };

    const capitalizeFirstLetter = (word) => {
        if (word) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        }
        return "";
    };

    const handleClose = useCallback(() => {
        onClose();
    }, [onClose]);

    const markNavChanged = (ids) => {
        if (!ids || ids.length === 0) return;
        setChangedNavIds((prev) => {
            const set = new Set(prev);
            ids.forEach((id) => set.add(id));
            return Array.from(set);
        });
    };

    const findPromotedNav = (fetchedNavs, content) => {
        if (fetchedNavs) {
            let tempNavAll = fetchedNavs;
            for (let el of tempNavAll) {
                const childIdx = el.children.findIndex(
                    (child) =>
                        child.title === content.title &&
                        (child.id ? child.id === content.id : true) //for new nav item we do not have an id, return true
                );
                el.children[childIdx] = {
                    ...el.children[childIdx],
                    promoted: childIdx >= 0,
                };
            }

            setNavAll(tempNavAll);
        }
    };

    const normalizeParentId = (value) => {
        if (!value) return null;
        if (typeof value === "object" && "id" in value) return value.id;
        return value;
    };

    const normalizeSlug = (value) => {
        if (!value || typeof value !== "string") return null;
        return value.replace(/^\/+|\/+$/g, "");
    };

    function prepareNavData(array) {
        if (array) {
            var result = [];
            array.forEach(function (a) {
                const parentId = normalizeParentId(a.parent);
                const iconFileId =
                    a.icon_file && typeof a.icon_file === "object"
                        ? a.icon_file.id
                        : a.icon_file || null;
                result.push({
                    id: a.id,
                    parent: parentId,
                    originalParent: parentId,
                    contentId: a.content.id,
                    contentSlug: a.content.slug,
                    title: a.content.title,
                    originalTitle: a.content.title,
                    type: a.content.type,
                    originalType: a.content.type,
                    position: a.position,
                    originalPosition: a.position,
                    icon: a.icon || "",
                    originalIcon: a.icon || "",
                    iconFileId: iconFileId,
                    originalIconFileId: iconFileId,
                    iconFile: a.icon_file || null,
                    children: Array.isArray(a.children)
                        ? prepareNavData(a.children)
                        : [],
                });
            });
            return result;
        }
    }

    function findNavElements(array, result) {
        let navElements = result ? result : [];
        if (array) {
            array.forEach(function (a) {
                const parentId = normalizeParentId(a.parent);
                const iconFileId =
                    a.icon_file && typeof a.icon_file === "object"
                        ? a.icon_file.id
                        : a.icon_file || null;
                let obj = {
                    id: a.id,
                    parent: parentId,
                    originalParent: parentId,
                    contentId: a.content.id,
                    contentSlug: a.content.slug,
                    title: a.content.title,
                    originalTitle: a.content.title,
                    type: a.content.type,
                    originalType: a.content.type,
                    position: a.position,
                    originalPosition: a.position,
                    icon: a.icon || "",
                    originalIcon: a.icon || "",
                    iconFileId: iconFileId,
                    originalIconFileId: iconFileId,
                    iconFile: a.icon_file || null,
                };

                if (NAV_TYPES.includes(obj.type)) {
                    navElements.push(obj);
                }

                if (Array.isArray(a.children) && a.children.length > 0) {
                    findNavElements(a.children, navElements);
                }
            });
        }
        return navElements;
    }

    const fetchData = useCallback(async () => {
        const updateRows = (files) => {
            if (files && files.length > 0) {
                const tempRows = [...files]
                    .sort((a, b) => {
                        const positionA =
                            typeof a.position === "number" ? a.position : 0;
                        const positionB =
                            typeof b.position === "number" ? b.position : 0;
                        return positionA - positionB;
                    })
                    .map((file) => ({
                        Id: file.id,
                        RowKey: `file-${file.id}`,
                        FileName: file.fileName,
                        FilePath: file.filePath,
                        FileSize: filesize(file.fileSize),
                        Type: file.type,
                        Position:
                            typeof file.position === "number" ? file.position : 0,
                    }));
                setRows(tempRows);
            }
        };
        try {
            setLoading(true);
            let res = null;
            let navData = null;
            if (slug && type === "edit") {
                const res = await get(
                    dispatch,
                    `/api/content/admin/edit/${slug}`
                );

                updateRows(res.data.files);
                setData({
                    ...data,
                    id: res.data.content.id,
                    type: res.data.content.type,
                    status: res.data.content.status,
                    news: false,
                    title: res.data.content.title,
                    body: res.data.content.body,
                    sticky: res.data.content.sticky,
                });
                setDialogTitle(
                    `${capitalizeFirstLetter(type)} ${res.data.content.type}`
                );
                if (res.data.thumbnail) {
                    setThumbnail({
                        id: res.data.thumbnail.id,
                        name: res.data.thumbnail.fileName,
                        size: res.data.thumbnail.fileSize,
                        type: res.data.thumbnail.type,
                        file: res.data.thumbnail.filePath,
                    });
                }
                navData = res.data.navAll;
                setSelectedGroups(res.data?.groups?.map(g => g.id) || []);

            } else if (contentType && type === "add") {
                let nextCreationContext = creationContext
                    ? { ...creationContext }
                    : null;
                const resolveNavContextBySlug = async (contextSlug) => {
                    const normalized = normalizeSlug(contextSlug);
                    if (!normalized) {
                        return null;
                    }
                    try {
                        const contextRes = await get(
                            dispatch,
                            `/api/content/nav/context/${encodeURIComponent(normalized)}`
                        );
                        return contextRes?.data || null;
                    } catch (navContextError) {
                        return null;
                    }
                };
                if (nextCreationContext && nextCreationContext.view && nextCreationContext.slug) {
                    if (nextCreationContext.view === "list") {
                        const listContext = await resolveNavContextBySlug(
                            nextCreationContext.slug
                        );
                        if (listContext?.nav_id != null) {
                            nextCreationContext.activeSlug = normalizeSlug(
                                nextCreationContext.slug
                            );
                            nextCreationContext.activeNavId = listContext.nav_id;
                            nextCreationContext.activeNavType =
                                listContext.nav_type || null;
                            nextCreationContext.targetParentId = listContext.nav_id;
                            nextCreationContext.targetParentType =
                                listContext.nav_type || null;
                        }
                    } else if (nextCreationContext.view === "detail") {
                        const detailContext = await resolveNavContextBySlug(
                            nextCreationContext.slug
                        );
                        if (detailContext?.nav_id != null) {
                            nextCreationContext.activeSlug = normalizeSlug(
                                nextCreationContext.slug
                            );
                            nextCreationContext.activeNavId = detailContext.nav_id;
                            nextCreationContext.activeNavType =
                                detailContext.nav_type || null;
                            nextCreationContext.targetParentId =
                                detailContext.parent_nav_id ?? null;
                            nextCreationContext.targetParentType =
                                detailContext.parent_nav_type || null;
                        }
                    } else if (
                        nextCreationContext.view === "tabbed" &&
                        Number.isInteger(Number(nextCreationContext.tabIndex)) &&
                        Number(nextCreationContext.tabIndex) > 0
                    ) {
                        try {
                            const tabsRes = await post(
                                dispatch,
                                `/api/content/content/`,
                                JSON.stringify({
                                    category: nextCreationContext.slug,
                                    sort_by: "popular",
                                })
                            );
                            const tabList = Array.isArray(tabsRes?.data)
                                ? tabsRes.data
                                : [];
                            const activeTab =
                                tabList[Number(nextCreationContext.tabIndex) - 1];
                            if (activeTab?.nav_id) {
                                nextCreationContext.activeNavId = activeTab.nav_id;
                            }
                            if (activeTab?.content?.slug) {
                                nextCreationContext.activeSlug =
                                    activeTab.content.slug;
                            }
                        } catch (tabContextError) {
                        }

                        const activeContextSlug = normalizeSlug(
                            nextCreationContext.activeSlug
                        );
                        const activeContext = await resolveNavContextBySlug(
                            activeContextSlug
                        );
                        if (activeContext?.nav_id != null) {
                            nextCreationContext.activeSlug = activeContextSlug;
                            nextCreationContext.activeNavId = activeContext.nav_id;
                            nextCreationContext.activeNavType =
                                activeContext.nav_type || null;
                            const shouldAttachToActiveNode =
                                activeContext.nav_type === "content_list" ||
                                activeContext.nav_type === "list";
                            nextCreationContext.targetParentId =
                                shouldAttachToActiveNode
                                    ? activeContext.nav_id
                                    : activeContext.parent_nav_id ?? null;
                            nextCreationContext.targetParentType =
                                shouldAttachToActiveNode
                                    ? activeContext.nav_type || null
                                    : activeContext.parent_nav_type || null;
                        }
                    }
                }
                setResolvedCreationContext(nextCreationContext);
                setData({
                    ...data,
                    id: -1,
                    type: contentType,
                    news: true,
                });
                setDialogTitle(`${capitalizeFirstLetter(type)} ${contentType}`);
                // new content
                res = await get(
                    dispatch,
                    `/api/content/admin/add/${contentType}`
                );
                navData = res.data;
                if (contentType !== "navigation") {
                    setNewNav({
                        id: -1,
                        title: "",
                        type: contentType,
                    });
                }
            } else {
                dispatch(
                    showAlert({
                        message: "Invalid input",
                        severity: "error",
                    })
                );
                handleClose();
            }
            setNavAll(prepareNavData(navData));
            setChangedNavIds([]);
            setNavElements(findNavElements(navData));
        } catch (error) {
            dispatch(
                showAlert({
                    message: error,
                    severity: "error",
                })
            );
            handleClose();
        } finally {
            setLoading(false);
        }
    }, [slug, type, handleClose, data, contentType, dispatch, creationContext]);

    useEffect(() => {
        if (open && !isOpened) {
            setIsOpened(true);
            // setData({
            //     ...data,
            //     userId: user.id,
            // });
            fetchData();
        } else if (!open && isOpened) {
            cleanup();
        }
    }, [open, isOpened, fetchData]);

    useEffect(() => {
        return () => {
            cleanup();
        };
    }, []);

    const onChange = (e) => {
        setData({ ...data, [e.target.name]: e.target.value });
    };

    const updateTitle = (e) => {
        const newTitle = e.target.value;
        if ((newNav && newNav.title === "") || newNav) {
            //update newNav if not dragged yet
            setNewNav({
                ...newNav,
                title: newTitle,
            });
        }
        if (navAll) {
            let tempNavAll = [...navAll];

            for (const el of tempNavAll) {
                const childIdx = el.children.findIndex(
                    (child) => child.title === data.title
                );
                if (childIdx >= 0) {
                    // found element
                    el.children[childIdx].title = newTitle;
                    break;
                }
            }

            setNavAll(tempNavAll);
        }

        onChange(e);
    };

    const updateBody = (newBody) => {
        setData({ ...data, body: newBody });
    };

    function replacer(key, value) {
        if (value instanceof Map) {
            return {
                dataType: "Map",
                // value: Array.from(value.entries()), // or with spread: value: [...value]
                value: [...value],
            };
        } else {
            return value;
        }
    }

    function prepareOutput(array) {
        var result = [];
        array.forEach(function (a, index) {
            const parentId = normalizeParentId(a.parent);
            const originalParentId = normalizeParentId(a.originalParent);
            result.push({
                ...a,
                parent: parentId,
                position: index,
                changed:
                    originalParentId !== parentId ||
                    a.originalTitle !== a.title ||
                    a.originalPosition !== index ||
                    a.originalType !== a.type ||
                    a.originalIcon !== a.icon ||
                    (a.originalIconFileId || null) !== (a.iconFileId || null),
            });
            if (Array.isArray(a.children)) {
                result = result.concat(prepareOutput(a.children));
            }
        });
        return result;
    }

    const buildNavMap = (nodes, map = new Map()) => {
        if (!Array.isArray(nodes)) return map;
        for (const node of nodes) {
            if (!node) continue;
            map.set(node.id, node);
            buildNavMap(node.children, map);
        }
        return map;
    };

    const isDescendantOf = (node, targetId) => {
        if (!node || !Array.isArray(node.children) || targetId == null) {
            return false;
        }
        for (const child of node.children) {
            if (child.id === targetId || isDescendantOf(child, targetId)) {
                return true;
            }
        }
        return false;
    };

    const validateNavChanges = (changedNodes, tree) => {
        if (!Array.isArray(changedNodes) || changedNodes.length === 0) {
            return { ok: true };
        }
        const map = buildNavMap(tree);
        for (const node of changedNodes) {
            if (!node) continue;
            const nodeType = node.type;
            const parentId = normalizeParentId(node.parent);
            const originalParentId = normalizeParentId(node.originalParent);

            // allow reordering within the same parent even if relation is legacy-invalid
            if (originalParentId === parentId) {
                continue;
            }
            if (parentId == null) {
                if (!ROOT_ALLOWED_TYPES.includes(nodeType)) {
                    return {
                        ok: false,
                        reason: `Invalid root node type: ${nodeType}.`,
                    };
                }
                continue;
            }
            const parentNode = map.get(parentId);
            if (!parentNode) {
                return {
                    ok: false,
                    reason: `Invalid parent reference for node id ${node.id}.`,
                };
            }
            const parentType = parentNode.type;
            const allowed = PARENT_CHILD_RULES[parentType] || [];
            if (!allowed.includes(nodeType)) {
                return {
                    ok: false,
                    reason: `Invalid relation: ${nodeType} under ${parentType}.`,
                };
            }
            const currentNode = map.get(node.id);
            if (currentNode && isDescendantOf(currentNode, parentId)) {
                return {
                    ok: false,
                    reason: `Cycle detected (node id ${node.id}).`,
                };
            }
            if (
                currentNode &&
                node.originalType &&
                node.originalType !== nodeType
            ) {
                const allowedChildren = PARENT_CHILD_RULES[nodeType] || [];
                const invalidChild =
                    currentNode.children &&
                    currentNode.children.find(
                        (child) => !allowedChildren.includes(child.type)
                    );
                if (invalidChild) {
                    return {
                        ok: false,
                        reason: `Invalid relation: ${invalidChild.type} under ${nodeType}.`,
                    };
                }
            }
        }
        return { ok: true };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const preparedNav =
            navAll && navAll.length
                ? prepareOutput(navAll).filter(
                    (nav) =>
                        nav.changed &&
                        (changedNavIds.includes(nav.id) ||
                            nav.originalParent === undefined)
                )
                : [];
        if (navAll && navAll.length) {
            const validation = validateNavChanges(preparedNav, navAll);
            if (!validation.ok) {
                dispatch(
                    showAlert({
                        message: validation.reason,
                        severity: "error",
                    })
                );
                return;
            }
        }

        const body = JSON.stringify(
            {
                data: { ...data, userId: user.id, groups: selectedGroups },
                uploadedFiles: uploadedFiles,
                removedFiles: removedFiles,
                modifiedFiles: modifiedFiles,
                thumbnail: thumbnail,
                nav: {
                    modified: preparedNav,
                    removed: removedNodes,
                },
            },
            replacer
        );

        setLoading(true);
        try {
            await post(dispatch, `/api/content/admin/${type}/`, body);

            submitStatus(true);
            window.location.reload();
        } catch (err) {
            dispatch(
                showAlert({
                    message: err,
                    severity: "error",
                })
            );
            submitStatus(false);
        } finally {
            setLoading(false);
        }
        // force to refetch data when dialog is reopened
        handleClose();
    };

    const getTitle = () => (
        <TextField
            id="outlined-basic"
            label={t("ContentDialog.getTitle")}
            variant="outlined"
            name="title"
            value={data && data.title ? data.title : ""}
            onChange={(e) => updateTitle(e)}
            fullWidth
        />
    );

    const getContent = () => (
        <RichTextEditor
            value={data && data.body ? data.body : null}
            setValue={updateBody}
            sx={{ margin: "2px" }}
        />
    );

    const getPublished = () => (
        <Stack spacing={2}>
            <GroupSelector
                selectedGroups={selectedGroups}
                setSelectedGroups={setSelectedGroups}
            />

            <FormControlLabel
                control={
                    <Switch
                        checked={data.status}
                        // value="active"
                        onChange={(e) => {
                            setData({
                                ...data,
                                status: e.target.checked,
                            });
                        }}
                    />
                }
                label={t("ContentDialog.getPublished.label")}
            />
            <FormControlLabel
                control={
                    <Switch
                        checked={data.news}
                        // value="active"
                        onChange={(e) => {
                            setData({
                                ...data,
                                news: e.target.checked,
                            });
                        }}
                    />
                }
                label={t("ContentDialog.getPublished.visibleInNews")}
            />
            <StickyForm
                content={data}
                setSticky={(newSticky) => {
                    setData({ ...data, sticky: newSticky });
                }}
            />
        </Stack>
    );

    const getNavigation = (id) => (
        <NavigationStep
            currentNav={id}
            navAll={navAll}
            addContext={
                type === "add"
                    ? resolvedCreationContext || creationContext
                    : null
            }
            setNavAll={(newNav) => {
                setNavAll(newNav);
            }}
            markNavChanged={markNavChanged}
            removeNode={{
                removedNodes: removedNodes,
                setRemovedNodes: setRemovedNodes,
            }}
            newNav={{
                newNode: newNav,
                setNewNode: setNewNav,
            }}
            setNewNav={setNewNav}
            navElements={navElements}
        />
    );

    const commonSteps = [
        { label: t('ContentDialog.commonSteps.editTitle'), content: getTitle(), optional: true },
        { label: t('ContentDialog.commonSteps.editContent'), content: getContent(), optional: true },
        {
            label: t('ContentDialog.commonSteps.attachments'),
            content: (
                <AttachmentsStep
                    slug={slug}
                    showErrorMessage={(err) =>
                        dispatch(
                            showAlert({
                                message: err,
                                severity: "error",
                            })
                        )
                    }
                    upload={{
                        uploadedFiles: uploadedFiles,
                        setUploadedFiles: setUploadedFiles,
                    }}
                    remove={{
                        removedFiles: removedFiles,
                        setRemovedFiles: setRemovedFiles,
                    }}
                    modified={{
                        modifiedFiles: modifiedFiles,
                        setModifiedFiles: setModifiedFiles,
                    }}
                    tableRows={{
                        rows: rows,
                        setRows: setRows,
                    }}
                />
            ),
            optional: true,
        },
        {
            label: t('ContentDialog.commonSteps.thumbnail'),
            content: (
                <ThumbnailStep
                    setThumbnail={setThumbnail}
                    attachments={rows}
                    uploadedFiles={uploadedFiles}
                    thumbnail={thumbnail}
                    showErrorMessage={(err) =>
                        dispatch(
                            showAlert({
                                message: err,
                                severity: "error",
                            })
                        )
                    }
                />
            ),
            optional: true,
        },
    ];
    const getSteps = () => {
        if (navAll) {
            return commonSteps.concat([
                {
                    label: t('ContentDialog.getSteps.editNavigation'),
                    content: getNavigation(data),
                    optional: true,
                },
            ]);
        }
        return commonSteps;
    };

    const isSingleStep = () => {
        return (
            contentType &&
            (contentType === "navigation" || contentType === "filters") &&
            type === "add"
        );
    };
    const getSingleStepContent = () => {
        if (contentType === "navigation") {
            return getNavigation();
        } else {
            return <></>;
        }
    };
    const renderDialogContent = () => {
        if (isSingleStep()) {
            return (
                <Fragment>
                    <Box sx={{ display: "flex", m: 2 }}>
                        {getSingleStepContent()}
                    </Box>
                    <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                        <Box sx={{ flex: "1 1 auto" }} />
                        <Button onClick={handleClose}>{t('buttonCommons.cancel')}</Button>
                        <Button onClick={handleSubmit}>{t('buttonCommons.submit')}</Button>
                    </Box>
                </Fragment>
            );
        }
        return (
            <Stepper
                steps={getSteps()}
                finalStep={getPublished()}
                onCancel={handleClose}
                onSubmit={handleSubmit}
            />
        );
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth={true}
            maxWidth="lg"
            height={"80vh"}
        >
            <DialogTitle>{dialogTitle && dialogTitle}</DialogTitle>
            <DialogContent>
                {loading ? <Loader /> : renderDialogContent()}
            </DialogContent>
        </Dialog>
    );
};

ContentDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    type: PropTypes.string.isRequired,
    contentType: PropTypes.string,
    slug: PropTypes.string,
    creationContext: PropTypes.shape({
        view: PropTypes.string,
        slug: PropTypes.string,
        tabIndex: PropTypes.number,
        activeSlug: PropTypes.string,
        activeNavId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        activeNavType: PropTypes.string,
        targetParentId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        targetParentType: PropTypes.string,
    }),
    submitStatus: PropTypes.func,
};

export default ContentDialog;
